// Real Blackrod, Bolton data for Blackrod Now
// Sources: 2025 annual assembly report, Town Council pages, organisation websites
// Where a field is not on the public record, it's intentionally left blank.

export const CATEGORIES = [
  "Family",
  "Youth",
  "Sport",
  "School",
  "Charity",
  "Business",
  "Community",
  "Music",
  "Food & Drink",
  "Volunteering",
  "Faith",
  "Heritage",
  "Health & Wellbeing",
];

export const ORG_TYPES = [
    "Clubs",
    "Schools",
    "Activity hosts",
    "Sports",
    "Community groups",
    "Youth groups",
    "Charities",
    "Churches/Faith groups",
    "Health & wellbeing",
    "Food & drink",
    "Heritage",
    "Venues",
    "Projects",
];

export const IMAGES = {
    hero: "https://images.unsplash.com/photo-1771257808250-fa5fbffa8d54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBldmVudCUyMGNyb3dkJTIwaGFwcHl8ZW58MHx8fHwxNzc4NzAyNjkyfDA&ixlib=rb-4.1.0&q=85",
    youthSport: "https://images.unsplash.com/photo-1777489689168-497290f235d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHx5b3V0aCUyMHNvY2NlciUyMHBhcmt8ZW58MHx8fHwxNzc4NzAyNzA2fDA&ixlib=rb-4.1.0&q=85",
    coffee: "https://images.unsplash.com/photo-1642026465180-5e25255838e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxsb2NhbCUyMGNvZmZlZSUyMHNob3AlMjBjb21tdW5pdHl8ZW58MHx8fHwxNzc4NzAyNzAxfDA&ixlib=rb-4.1.0&q=85",
    spotlight: "https://images.unsplash.com/photo-1777115213572-2097c1c9ebcc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxjb21tdW5pdHklMjBldmVudCUyMGNyb3dkJTIwaGFwcHl8ZW58MHx8fHwxNzc4NzAyNjkyfDA&ixlib=rb-4.1.0&q=85",
    street: "https://images.unsplash.com/photo-1641907623702-a29361c36367?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxlbmdsYW5kJTIwdmlsbGFnZSUyMHN0cmVldHxlbnwwfHx8fDE3Nzg3MDI3MDZ8MA&ixlib=rb-4.1.0&q=85",
};

// ---------- Date helpers ----------
const today = new Date();

// Relative date — n days from today at hh:mm
const dt = (daysFromToday, hour = 10, minute = 0) => {
    const d = new Date(today);
    d.setDate(d.getDate() + daysFromToday);
    d.setHours(hour, minute, 0, 0);
    return d.toISOString();
};

// Next occurrence of a weekday (0=Sun, 1=Mon, ..., 6=Sat) at hh:mm
const nextWeekday = (targetDay, hour = 10, minute = 0) => {
    const d = new Date();
    const diff = (targetDay + 7 - d.getDay()) % 7;
    d.setDate(d.getDate() + (diff === 0 ? 7 : diff));
    d.setHours(hour, minute, 0, 0);
    return d.toISOString();
};

// Absolute date — uses local time
const onDate = (yyyy, mm, dd, hh = 10, mi = 0) =>
    new Date(yyyy, mm - 1, dd, hh, mi, 0, 0).toISOString();

const addHours = (iso, hours) =>
    new Date(new Date(iso).getTime() + hours * 60 * 60 * 1000).toISOString();

// ---------- ORGANISATIONS ----------
export const ORGANISATIONS = [
    {
        slug: "blackrod-town-council",
        name: "Blackrod Town Council",
        category: "Community groups",
        tags: ["Civic", "Community", "Council"],
        short: "Blackrod's civic body — public meetings, community planning and event funding.",
        about:
            "Blackrod Town Council represents the residents of Blackrod, funds and convenes community events, supports local projects and manages the village's significant dates. Made up of 9 councillors with public meetings held monthly — members of the public are welcome to attend.",
        does: "Funds and convenes major village events (Festival of Blackrod, Christmas Light Switch-On, Christmas Market), supports community projects and represents residents.",
        forWho: "All Blackrod residents.",
        meeting: "Public council meetings monthly — open to the public",
        address: "Blackrod Library / Council Building, Church Street, BL6 5EQ",
        location: "Blackrod",
        email: "clerk@blackrodtowncouncil.gov.uk",
        phone: "01204 696362",
        website: "https://www.blackrodtowncouncil.gov.uk",
        socials: { facebook: "#" },
        brandColor: "#0052FF",
        logo: "🏛️",
        cover: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=1200&q=80",
        upcoming: 3,
        status: "approved",
    },
    {
        slug: "blackrod-neighbourhood-plan-delivery-group",
        name: "Blackrod Neighbourhood Plan Delivery Group",
        category: "Projects",
        tags: ["Community", "Planning"],
        short: "Residents' policy group shaping Blackrod's future — rights of way, MUGA, Local Plan.",
        about:
            "A residents' planning and policy group continuing strategic work on rights of way, village audits, the MUGA (multi-use games area) concept and Local Plan engagement.",
        does: "Strategic neighbourhood planning, rights-of-way audits, MUGA concept work, Local Plan engagement.",
        forWho: "Blackrod residents.",
        meeting: "Via the Town Council — schedule unpublished",
        address: "c/o Blackrod Town Council, Church Street, BL6 5EQ",
        location: "Blackrod",
        email: "clerk@blackrodtowncouncil.gov.uk",
        phone: "",
        website: "https://www.blackrodtowncouncil.gov.uk",
        socials: {},
        brandColor: "#1d4ed8",
        logo: "🗺️",
        cover: "https://images.unsplash.com/photo-1551801841-ecad875a5142?w=1200&q=80",
        upcoming: 0,
        status: "approved",
    },
    {
        slug: "horwich-blackrod-community-events-cic",
        name: "Horwich & Blackrod Community Events CIC",
        category: "Projects",
        tags: ["Community", "Events"],
        short: "The community events organiser behind Blackrod's biggest 2025 days out.",
        about:
            "A Community Interest Company delivering Blackrod's major public events in partnership with the Town Council. Across all Horwich + Blackrod 2025 events, the CIC welcomed approximately 18,400 guests. Confirmed to deliver Blackrod's 2026 events.",
        does: "Delivers the Festival of Blackrod, Christmas Market and other large community events.",
        forWho: "Everyone in Blackrod and surrounding areas.",
        meeting: "",
        address: "Blackrod",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: { facebook: "#" },
        brandColor: "#FF3366",
        logo: "🎪",
        cover: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200&q=80",
        upcoming: 3,
        status: "approved",
    },
    {
        slug: "blackrod-sports-community-centre",
        name: "Blackrod Sports and Community Centre",
        category: "Community groups",
        tags: ["Community", "Sport", "Charity", "Venue"],
        short:
            "Charity-run community hub on Greenbarn Way — classes, support, sport and seasonal events.",
        about:
            "A charity-run community centre offering a packed weekly timetable: walking football, ICT, line dancing, pilates, tea & chat, dementia support, an art group, fitness sessions, table tennis, karate and youth/sports sessions. In 2025 the centre ran free seasonal activities and gift giveaways, and co-funded a seniors' summer event. Approximately 1,865 weekly users and 6,595 monthly users as of February 2025.",
        does: "Weekly classes & clubs, seasonal events, gift giveaways and a venue for community training (MHFA, first aid).",
        forWho: "Everyone in Blackrod, from toddlers to seniors.",
        meeting: "Weekly timetable — see the centre's What's On",
        address: "Greenbarn Way, Blackrod BL6 5TG",
        location: "Blackrod",
        email: "bsacc.manager@gmail.com",
        phone: "07980 713289",
        website: "",
        socials: { facebook: "#" },
        brandColor: "#16a34a",
        logo: "🏟️",
        cover: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=1200&q=80",
        upcoming: 5,
        status: "approved",
    },
    {
        slug: "st-katharines-church",
        name: "St Katharine's Church, Blackrod",
        category: "Churches/Faith groups",
        tags: ["Faith", "Community", "Family"],
        short:
            "A welcoming village church and host to a huge range of community groups — Messy Church, Wiggle Worship, choir, reading club and more.",
        about:
            "St Katharine's is a busy village church hosting community groups, regular services, Messy Church, Wiggle Worship Baby & Toddler Group, a reading club, choir, flower club, film club and other gatherings. 111 names on the 2026 electoral roll. The church is a major host venue for community groups in Blackrod.",
        does: "Sunday Eucharist 11am · Second-Sunday all-age worship 9.30am · Tuesday Holy Communion 9.30am · many weekday groups.",
        forWho: "Everyone — of any faith or none.",
        meeting: "Sunday Eucharist 11am · Tuesday Holy Communion 9.30am",
        address: "Church Street, Blackrod BL6 5TE",
        location: "Blackrod",
        email: "Revawynne@gmail.com",
        phone: "01204 468150",
        website: "https://www.stkatharine.net",
        socials: { facebook: "#" },
        brandColor: "#7c3aed",
        logo: "⛪",
        cover: "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=1200&q=80",
        upcoming: 8,
        status: "approved",
    },
    {
        slug: "blackrod-bloomers",
        name: "Blackrod Bloomers",
        category: "Community groups",
        tags: ["Volunteering", "Community", "Outdoor"],
        short: "The volunteer gardeners keeping Blackrod's gateways, planters and flower beds blooming.",
        about:
            "A volunteer planting and beautification group caring for Blackrod's gateways, planters, flower beds and (seasonally) the cemetery. 7 committed members in the 2025 assembly report. The group fundraises through coffee mornings and a 'good as new' clothes sale for the Children's Society. Always open to new volunteers and donations.",
        does: "Planting and maintaining gateways, planters and flower beds across the village; seasonal cemetery work; community fundraising events.",
        forWho: "Anyone happy to roll up their sleeves.",
        meeting: "Wednesdays & Fridays at 8am; summer evening work also runs",
        address: "Across Blackrod village",
        location: "Blackrod",
        email: "clerk@blackrodtowncouncil.gov.uk",
        phone: "01204 696362",
        website: "",
        socials: { facebook: "#" },
        brandColor: "#16a34a",
        logo: "🌷",
        cover: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=1200&q=80",
        upcoming: 2,
        status: "approved",
    },
    {
        slug: "blackrod-scarecrow-festival-committee",
        name: "Blackrod Scarecrow Festival Committee",
        category: "Community groups",
        tags: ["Community", "Family", "Festival", "Heritage"],
        short:
            "Organisers of Blackrod's much-loved annual Scarecrow Festival — workshops, scarecrows and a Sunday 10k.",
        about:
            "The committee behind Blackrod's annual Scarecrow Festival, with scarecrow-making workshops in the run-up (at Tree Barn) and a packed weekend across the village, including a Sunday 10k starting at Hilltops.",
        does: "Hosts spring workshops at Tree Barn and the summer Scarecrow Festival.",
        forWho: "Everyone — all ages.",
        meeting: "",
        address: "6 Nightingale Road, Blackrod BL6 5DX",
        location: "Blackrod",
        email: "jeanhibbert@virginmedia.com",
        phone: "01204 691575",
        website: "",
        socials: { facebook: "#" },
        brandColor: "#f59e0b",
        logo: "👨‍🌾",
        cover: "https://images.unsplash.com/photo-1601999103894-79c6c4f6ba0d?w=1200&q=80",
        upcoming: 1,
        status: "approved",
    },
    {
        slug: "blackrod-community-group",
        name: "Blackrod Community Group",
        category: "Community groups",
        tags: ["Community", "Fundraising"],
        short:
            "Bi-monthly community association — fundraising bingo evenings and an annual fashion show.",
        about:
            "A community association holding bi-monthly meetings, fundraising bingo evenings and organising an annual fashion show.",
        does: "Bi-monthly community meetings, fundraising bingo and special social events.",
        forWho: "All village members.",
        meeting: "Bi-monthly",
        address: "Blackrod",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: { facebook: "#" },
        brandColor: "#dc2626",
        logo: "🎲",
        cover: "https://images.unsplash.com/photo-1543007630-9710e4a00a20?w=1200&q=80",
        upcoming: 0,
        status: "approved",
    },
    {
        slug: "blackrod-art-group",
        name: "Blackrod Art Group",
        category: "Clubs",
        tags: ["Heritage", "Community", "Charity"],
        short: "A weekly arts club with library exhibitions, scarecrow-weekend stalls and charity fundraising.",
        about:
            "A weekly arts club meeting at Blackrod Sports & Community Centre. The group exhibits during the Scarecrow Festival Saturday, runs a 12-week library exhibition each year and raises money through charity fundraising. Over 40 members on the Bolton CVS record; a record 56 members in the 2025 assembly report.",
        does: "Weekly art sessions; annual library exhibition; Scarecrow Festival weekend exhibition; charity fundraising.",
        forWho: "Adults of all abilities.",
        meeting: "Weekly at Blackrod Sports & Community Centre",
        address: "Blackrod Sports & Community Centre, Greenbarn Way, BL6 5TG",
        location: "Blackrod",
        email: "blackrodartgroup@gmail.com",
        phone: "07894 904100",
        website: "",
        socials: {},
        brandColor: "#FF3366",
        logo: "🎨",
        cover: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=1200&q=80",
        upcoming: 1,
        status: "approved",
    },
    {
        slug: "girlguiding-blackrod",
        name: "Girlguiding in Blackrod",
        category: "Youth groups",
        tags: ["Youth", "Girls"],
        short: "Rainbows, Brownies, Guides and Rangers based at Blackrod Primary School.",
        about:
            "Blackrod's Girlguiding units have healthy numbers across Rainbows, Brownies, Guides and Rangers, continuing to take part in community and district events.",
        does: "Weekly section meetings, badge work, trips, community events.",
        forWho: "Girls aged 4 to 18 (Rainbows → Rangers).",
        meeting: "Various — by section",
        address: "Blackrod Primary School",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "https://www.girlguiding.org.uk/information-for-parents/register-your-daughter/",
        socials: {},
        brandColor: "#0ea5e9",
        logo: "🌈",
        cover: "https://images.unsplash.com/photo-1529070538774-1843cb3265df?w=1200&q=80",
        upcoming: 0,
        status: "approved",
    },
    {
        slug: "blackrod-local-history-group",
        name: "Blackrod Local History Group",
        category: "Heritage",
        tags: ["Heritage", "Community"],
        short:
            "Monthly history meetings at the library — £1 entry or £10 annual membership, refreshments included.",
        about:
            "Regular monthly meetings at Blackrod Library / Council Chamber with a six-month printed programme and a monthly newsletter. Average attendance about 40; newsletter to 80+ email addresses.",
        does: "Talks on Blackrod and Bolton-area history, a six-month printed programme, a monthly newsletter.",
        forWho: "Anyone interested in local history.",
        meeting: "Regular monthly meetings (see programme)",
        address: "Blackrod Library / Council Chamber, Church Street, BL6 5EQ",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: { facebook: "#" },
        brandColor: "#0f766e",
        logo: "📜",
        cover: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=1200&q=80",
        upcoming: 1,
        status: "approved",
    },
    {
        slug: "blackrod-community-choir",
        name: "Blackrod Community Choir",
        category: "Clubs",
        tags: ["Music", "Community", "Charity"],
        short:
            "A friendly Saturday-morning community choir at St Katharine's — no audition, first session free.",
        about:
            "Founded six years ago, the choir meets at St Katharine's every Saturday at 10am for one hour. No audition. First session is free as a taster. Numbers are increasing and there's no upper limit. 2025 saw village performances and an appearance at Chorley Live, alongside charitable fundraising.",
        does: "Weekly singing, community performances, charity fundraising.",
        forWho: "Anyone who enjoys singing.",
        meeting: "Saturdays 10am – 11am",
        address: "St Katharine's Church, Church Street, BL6 5TE",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "https://www.stkatharine.net",
        socials: { facebook: "#" },
        brandColor: "#9333ea",
        logo: "🎤",
        cover: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1200&q=80",
        upcoming: 4,
        status: "approved",
    },
    {
        slug: "blackrod-community-walking-group",
        name: "Blackrod Community Walking Group",
        category: "Health & wellbeing",
        tags: ["Health & Wellbeing", "Outdoor", "Community"],
        short: "Monthly local walks and rambles — usually with lunch afterwards.",
        about:
            "A monthly local walks and rambles group, usually with lunch afterwards. Numbers vary; a January 2026 walk had 13 walkers.",
        does: "Monthly community walks; sociable lunch after most walks.",
        forWho: "Anyone — pace usually friendly.",
        meeting: "Monthly — see group page for the next walk",
        address: "Meeting point varies",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: { facebook: "#" },
        brandColor: "#16a34a",
        logo: "🥾",
        cover: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=1200&q=80",
        upcoming: 1,
        status: "approved",
    },
    {
        slug: "hilltops-bowling-club",
        name: "Hilltops Bowling Club",
        category: "Sports",
        tags: ["Sport"],
        short: "Crown green bowls — five leagues, three match days a week, plus open days for newcomers.",
        about:
            "Matches three days a week across five leagues, plus annual open days offering bowls/coaching. 30 members. Bowlers must also be members of the Sports & Social Club to use facilities.",
        does: "League matches, coaching, annual open days.",
        forWho: "Bowls players — members of Hilltops Sports & Social Club.",
        meeting: "Match days three times per week",
        address: "Hilltops Sports & Social Club, Blackrod",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: {},
        brandColor: "#16a34a",
        logo: "🎳",
        cover: "https://images.unsplash.com/photo-1551958219-acbc608c6377?w=1200&q=80",
        upcoming: 0,
        status: "approved",
    },
    {
        slug: "talking-football",
        name: "Talking Football",
        category: "Health & wellbeing",
        tags: ["Health & Wellbeing", "Sport", "Charity", "Men"],
        short:
            "Men's wellbeing through football — matches, mental-health workshops and charity sport.",
        about:
            "A men's wellbeing, sport and social support group. Matches, mental-health workshops, local charity sport and a planned charity match and band-night fundraiser. More than 200 men have accessed the group since April 2022.",
        does: "Weekly play, mental-health workshops, charity sport and fundraisers.",
        forWho: "Men (any age, any ability).",
        meeting: "Mondays & Wednesdays 7pm – 8pm",
        address: "Bolton Arena",
        location: "Bolton",
        email: "",
        phone: "",
        website: "",
        socials: { facebook: "#", instagram: "#" },
        brandColor: "#0052FF",
        logo: "⚽",
        cover: "https://images.unsplash.com/photo-1517649763962-0c623066013b?w=1200&q=80",
        upcoming: 2,
        status: "approved",
    },
    {
        slug: "popplewell-trust",
        name: "Popplewell Trust",
        category: "Charities",
        tags: ["Charity", "School"],
        short: "Educational charity supporting Blackrod's schools with annual recognition and project funding.",
        about:
            "An educational charity working closely with Blackrod's two local schools, with library and Town Clerk support. Strongly active, though not event-led in a public-marketing sense.",
        does: "Annual school-related recognition and project support.",
        forWho: "Local schools and their pupils.",
        meeting: "",
        address: "c/o Blackrod Library, Church Street, BL6 5EQ",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: {},
        brandColor: "#7c3aed",
        logo: "🎓",
        cover: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&q=80",
        upcoming: 0,
        status: "approved",
    },
    {
        slug: "blackrod-educational-trust",
        name: "Blackrod Educational Trust",
        category: "Charities",
        tags: ["Charity", "School", "Family"],
        short: "Annual grants for educational support — in 2024 the Trust gave ~£10,000 to 49 families.",
        about:
            "An educational charity running an annual grant round, handled through the Town Clerk's office with library post-box support. In 2024: 89 applications from 49 families; around £10,000 granted. Criteria may be reviewed for the next year.",
        does: "Annual grant applications for educational support to families and individuals.",
        forWho: "Blackrod families and individuals.",
        meeting: "",
        address: "c/o Town Clerk, Blackrod Library, Church Street, BL6 5EQ",
        location: "Blackrod",
        email: "clerk@blackrodtowncouncil.gov.uk",
        phone: "01204 696362",
        website: "",
        socials: {},
        brandColor: "#1d4ed8",
        logo: "📚",
        cover: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200&q=80",
        upcoming: 0,
        status: "approved",
    },
    {
        slug: "blackrod-cricket-club",
        name: "Blackrod Cricket Club",
        category: "Sports",
        tags: ["Sport", "Community", "Junior"],
        short: "Adult, junior and girls' cricket on Vicarage Road — venue for Festival of Blackrod.",
        about:
            "A busy playing club and major venue host on Vicarage Road. Multiple adult, junior and girls' teams visible on Play-Cricket. Hosted the Festival of Blackrod 2025.",
        does: "League cricket — adult, junior and girls' teams; venue hire for community events.",
        forWho: "Players from junior level upwards; everyone for events.",
        meeting: "Match days & training — see Play-Cricket",
        address: "Vicarage Road, Blackrod BL6 5AA",
        location: "Blackrod",
        email: "wafc78@hotmail.com",
        phone: "",
        website: "",
        socials: { facebook: "#" },
        brandColor: "#0f766e",
        logo: "🏏",
        cover: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=1200&q=80",
        upcoming: 1,
        status: "approved",
    },
    {
        slug: "st-katharines-stitch-knit",
        name: "St Katharine's Stitch & Knit Group",
        category: "Clubs",
        tags: ["Community", "Charity", "Craft"],
        short:
            "Monday-morning stitch and knit at St Katharine's — open to novices and newcomers.",
        about:
            "A social craft and charity group meeting in the St Katharine's upper room. Open to novices and newcomers; around 12–13 weekly. Charitable knitting/output confirmed throughout 2025.",
        does: "Weekly stitching and knitting; items made and sold at events for charity.",
        forWho: "Anyone who knits, stitches, or wants to learn.",
        meeting: "Mondays 10am – 12 noon",
        address: "St Katharine's Church, Church Street, BL6 5TE",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "https://www.stkatharine.net",
        socials: {},
        brandColor: "#9333ea",
        logo: "🧶",
        cover: "https://images.unsplash.com/photo-1517157212532-c5ee2cb55c01?w=1200&q=80",
        upcoming: 4,
        status: "approved",
    },
    {
        slug: "messy-church-st-katharines",
        name: "Messy Church at St Katharine's",
        category: "Churches/Faith groups",
        tags: ["Family", "Faith", "Free"],
        short: "Last-Saturday family Messy Church — craft, story and a meal. Free.",
        about:
            "Ongoing monthly Messy Church sessions for families. Around 30–35 children and 30 adults, with about 20 volunteers each session.",
        does: "Craft, story, song and a meal — for families.",
        forWho: "Families with children.",
        meeting: "Last Saturday of each month, 4pm – 6pm",
        address: "St Katharine's Church, Church Street, BL6 5TE",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "https://www.stkatharine.net",
        socials: {},
        brandColor: "#FF3366",
        logo: "🎨",
        cover: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=1200&q=80",
        upcoming: 1,
        status: "approved",
    },
    {
        slug: "wiggle-worship",
        name: "Wiggle Worship Baby & Toddler Group",
        category: "Community groups",
        tags: ["Family", "Faith", "Free"],
        short: "Free term-time Monday morning group for babies and toddlers (with Baby Rhyme).",
        about:
            "Regular weekly sessions for parents and toddlers, with linked Baby Rhyme support. By early 2026 the combined session ran from 9am to 11.15am. Averaged over 20 children in 2025.",
        does: "Songs, play, faith stories and a brew for parents.",
        forWho: "Parents and carers with babies & toddlers.",
        meeting: "Mondays in term time, 9am – 11.15am",
        address: "St Katharine's Church, Church Street, BL6 5TE",
        location: "Blackrod",
        email: "",
        phone: "07514 041611",
        website: "https://www.stkatharine.net",
        socials: {},
        brandColor: "#f59e0b",
        logo: "🍼",
        cover: "https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?w=1200&q=80",
        upcoming: 4,
        status: "approved",
    },
    {
        slug: "monday-night-reading-club",
        name: "St Katharine's Monday Night Reading Club",
        category: "Clubs",
        tags: ["Community", "Free"],
        short: "Last-Monday-of-the-month reading club at St Katharine's — free and friendly.",
        about: "A regular monthly evening reading club, free and open to new members.",
        does: "Monthly book discussion.",
        forWho: "Anyone who reads.",
        meeting: "Last Monday of the month, 7.30pm – 9pm",
        address: "St Katharine's Church, Church Street, BL6 5TE",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "https://www.stkatharine.net",
        socials: {},
        brandColor: "#7c3aed",
        logo: "📖",
        cover: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=1200&q=80",
        upcoming: 1,
        status: "approved",
    },
    {
        slug: "connect-st-katharines",
        name: "Connect at St Katharine's",
        category: "Health & wellbeing",
        tags: ["Community", "Free", "Warm space"],
        short: "Tuesday-morning warm space at St Katharine's — free refreshments and a friendly chat.",
        about: "Ongoing regular warm-space activity — mainly retired village members. Free refreshments.",
        does: "Warm space, a brew, and good company.",
        forWho: "Mainly retired villagers, but everyone welcome.",
        meeting: "Tuesdays 10.30am for two hours",
        address: "St Katharine's Church, Church Street, BL6 5TE",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "https://www.stkatharine.net",
        socials: {},
        brandColor: "#0ea5e9",
        logo: "☕",
        cover: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1200&q=80",
        upcoming: 4,
        status: "approved",
    },
    {
        slug: "knit-natter-library",
        name: "Knit & Natter Group",
        category: "Clubs",
        tags: ["Community", "Craft"],
        short: "Saturday morning knit & natter at Blackrod Library — average 13 attendees.",
        about: "A library-based craft and social group; held a stall during Scarecrow Festival weekend and ran workshops/commissions in 2025.",
        does: "Knitting, nattering, occasional workshops and event stalls.",
        forWho: "Anyone who likes to knit and chat.",
        meeting: "Saturdays 10.30am – 1.00pm",
        address: "Blackrod Library, Church Street, BL6 5EQ",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: {},
        brandColor: "#16a34a",
        logo: "🧵",
        cover: "https://images.unsplash.com/photo-1565693413579-8a73bdee0a2c?w=1200&q=80",
        upcoming: 2,
        status: "approved",
    },
    {
        slug: "twilight-book-club",
        name: "Twilight Book Club in the Library",
        category: "Clubs",
        tags: ["Community", "Free"],
        short: "Third-Thursday book club at Blackrod Library — friendly evening discussion.",
        about: "A library book club averaging 10–15 attendees.",
        does: "Monthly book discussion.",
        forWho: "Adult readers.",
        meeting: "Third Thursday of the month, 5.45pm",
        address: "Blackrod Library, Church Street, BL6 5EQ",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: {},
        brandColor: "#1d4ed8",
        logo: "🌒",
        cover: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200&q=80",
        upcoming: 1,
        status: "approved",
    },
    {
        slug: "blackrod-young-peoples-centre",
        name: "Blackrod Young People's Centre",
        category: "Youth groups",
        tags: ["Youth", "Family", "Free"],
        short: "Free youth and play sessions for children aged 4 – 17 (reception+).",
        about:
            "Free junior youth club for ages 9 – 12, free play activities for ages 4 – 12 (reception+), and a free youth club for ages 13 – 17.",
        does: "Free youth & play sessions across age bands.",
        forWho: "Young people aged 4 – 17.",
        meeting: "Sessions across the week — see Bolton Council directory",
        address: "Blackrod",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: {},
        brandColor: "#FF3366",
        logo: "🎮",
        cover: "https://images.unsplash.com/photo-1529070538774-1843cb3265df?w=1200&q=80",
        upcoming: 0,
        status: "approved",
    },
    {
        slug: "blackrod-amateur-boxing-club",
        name: "Blackrod Amateur Boxing Club",
        category: "Sports",
        tags: ["Sport", "Youth"],
        short: "Boxing sessions for young people from age 9, coached by Rick Godding.",
        about:
            "Amateur boxing for young people from age 9+, coached by Rick Godding. Sessions held at Blackrod Sports & Community Centre by arrangement.",
        does: "Youth boxing coaching.",
        forWho: "Young people aged 9 and up.",
        meeting: "By arrangement with the coach",
        address: "Blackrod Sports & Community Centre, Greenbarn Way, BL6 5TG",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: {},
        brandColor: "#dc2626",
        logo: "🥊",
        cover: "https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=1200&q=80",
        upcoming: 0,
        status: "approved",
    },
    {
        slug: "douglas-valley-golf-club",
        name: "Douglas Valley Golf Club",
        category: "Activity hosts",
        org_type: "business_host",
        tags: ["Sport"],
        short: "Active Blackrod golf venue on the Blackrod by-pass.",
        about: "Active golf venue serving Blackrod and the wider area.",
        does: "Golf — play, lessons, membership.",
        forWho: "Golfers.",
        meeting: "",
        address: "Blackrod by-pass, Blackrod",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: {},
        brandColor: "#16a34a",
        logo: "⛳",
        cover: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1200&q=80",
        upcoming: 0,
        status: "approved",
    },
    {
        slug: "blackrod-church-school-ptfa",
        name: "Blackrod Church School PTFA",
        category: "Schools",
        tags: ["School", "Family", "Fundraising"],
        short:
            "Parent-Teacher-Friends Association — Glow Disco, Valentine's Disco, Easter Bingo and more.",
        about:
            "The PTFA at Blackrod Church School runs family events including a Glow Disco, Christmas activities, a Valentine's Disco and an Easter Bingo.",
        does: "Family-friendly school fundraisers and seasonal events.",
        forWho: "School families and the wider community.",
        meeting: "",
        address: "Blackrod Church School, Blackrod",
        location: "Blackrod",
        email: "",
        phone: "",
        website: "",
        socials: { facebook: "#" },
        brandColor: "#7c3aed",
        logo: "🎒",
        cover: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&q=80",
        upcoming: 0,
        status: "approved",
    },
];

// ---------- EVENTS ----------
// Mix of real dated 2025/2026 events from the report + near-term recurring sessions
// derived from the published meeting times.

export const EVENTS = [
    // ===== 2026 FUTURE FIXED-DATE EVENTS =====
    {
        id: "evt-festival-2026",
        title: "Festival of Blackrod 2026",
        orgSlug: "horwich-blackrod-community-events-cic",
        category: "Community",
        start: onDate(2026, 9, 12, 12, 0),
        end: onDate(2026, 9, 12, 22, 0),
        venue: "Blackrod Cricket Club (TBC)",
        address: "Vicarage Road, Blackrod BL6 5AA",
        description:
            "The Festival of Blackrod returns for 2026 — live music, street food, children's activities, community stalls, fairground rides and games. In 2025 the festival drew around 4,400 attendees. Formally approved in the February 2026 Town Council minutes and listed on the 2026–2027 significant dates schedule. Free to attend.",
        cost: "Free to attend (food/bar payable on site)",
        age: "All ages",
        accessibility: "Outdoor ground — please contact organisers for specific access needs",
        booking: "",
        contactEmail: "clerk@blackrodtowncouncil.gov.uk",
        contactPhone: "01204 696362",
        image: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200&q=80",
        featured: true,
        status: "approved",
    },
    {
        id: "evt-light-switchon-2026",
        title: "Blackrod Christmas Light Switch-On 2026",
        orgSlug: "blackrod-town-council",
        category: "Community",
        start: onDate(2026, 11, 24, 18, 30),
        end: onDate(2026, 11, 24, 20, 0),
        venue: "TBC — see Town Council updates",
        address: "Blackrod",
        description:
            "The annual Christmas Light Switch-On returns. Listed on the official Town Council 2026–2027 significant dates schedule. The 2025 switch-on featured festive singing and refreshments afterwards in the Blackhorse Street Gardens — expect a similar warm welcome in 2026.",
        cost: "Free",
        age: "All ages",
        accessibility: "Outdoor village event",
        booking: "",
        contactEmail: "clerk@blackrodtowncouncil.gov.uk",
        contactPhone: "01204 696362",
        image: "https://images.unsplash.com/photo-1606830733744-0ad778449672?w=1200&q=80",
        featured: true,
        status: "approved",
    },
    {
        id: "evt-christmas-market-2026",
        title: "Blackrod Christmas Market 2026",
        orgSlug: "horwich-blackrod-community-events-cic",
        category: "Community",
        start: onDate(2026, 11, 28, 10, 0),
        end: onDate(2026, 11, 28, 16, 0),
        venue: "Church Street, Blackrod (TBC)",
        address: "Church Street, Blackrod",
        description:
            "Blackrod's Christmas Market returns in 2026 — local stalls, festive treats and family activities along Church Street. Free to attend, like all HBCE-hosted events in 2025.",
        cost: "Free to attend",
        age: "All ages",
        accessibility: "Outdoor street event",
        booking: "",
        contactEmail: "clerk@blackrodtowncouncil.gov.uk",
        contactPhone: "01204 696362",
        image: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=1200&q=80",
        featured: true,
        status: "approved",
    },

    // ===== 2025 PAST EVENTS (archived/historic on the calendar) =====
    {
        id: "evt-festival-2025",
        title: "Festival of Blackrod 2025",
        orgSlug: "horwich-blackrod-community-events-cic",
        category: "Community",
        start: onDate(2025, 9, 27, 12, 0),
        end: onDate(2025, 9, 27, 22, 0),
        venue: "Blackrod Cricket Club",
        address: "Vicarage Road, Blackrod BL6 5AA",
        description:
            "Live music, street food, children's activities, community stalls, a strong-man competition, rides and games. Around 4,400 attendees — total cost £18,753.27 (£7,000 from the Town Council plus sponsorship). Free to attend.",
        cost: "Free to attend",
        age: "All ages",
        accessibility: "Outdoor ground",
        booking: "",
        contactEmail: "clerk@blackrodtowncouncil.gov.uk",
        contactPhone: "01204 696362",
        image: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-scarecrow-2025",
        title: "Blackrod Scarecrow Festival",
        orgSlug: "blackrod-scarecrow-festival-committee",
        category: "Community",
        start: onDate(2025, 7, 5, 10, 0),
        end: onDate(2025, 7, 5, 19, 0),
        venue: "Across Blackrod",
        address: "Blackrod",
        description:
            "The annual village scarecrow festival, 10am–7pm on both Saturday and Sunday. Includes a Sunday 10k starting at Hilltops. Art group exhibition, knit & natter stall, scarecrows across the village.",
        cost: "Free entry",
        age: "All ages",
        accessibility: "Outdoor village event",
        booking: "",
        contactEmail: "jeanhibbert@virginmedia.com",
        contactPhone: "01204 691575",
        image: "https://images.unsplash.com/photo-1601999103894-79c6c4f6ba0d?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-scarecrow-2025-sun",
        title: "Scarecrow Festival — Sunday 10k",
        orgSlug: "blackrod-scarecrow-festival-committee",
        category: "Sport",
        start: onDate(2025, 7, 6, 10, 0),
        end: onDate(2025, 7, 6, 19, 0),
        venue: "Starts at Hilltops",
        address: "Hilltops Sports & Social Club, Blackrod",
        description:
            "Day two of the Scarecrow Festival — including the Sunday 10k starting at Hilltops, plus scarecrows across the village from 10am to 7pm.",
        cost: "Race entry payable; festival viewing free",
        age: "All ages",
        accessibility: "Outdoor",
        booking: "",
        contactEmail: "jeanhibbert@virginmedia.com",
        contactPhone: "01204 691575",
        image: "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-ve80-concert",
        title: "VE Day 80th Anniversary Concert",
        orgSlug: "st-katharines-church",
        category: "Music",
        start: onDate(2025, 5, 11, 19, 0),
        end: onDate(2025, 5, 11, 21, 0),
        venue: "St Katharine's Church",
        address: "Church Street, Blackrod BL6 5TE",
        description:
            "A VE Day 80th Anniversary concert — one of the Town Council's two big free-to-attend events for 2025, hosted at St Katharine's in partnership with Horwich & Blackrod Community Events CIC. Ticketed for capacity; sold out on the night.",
        cost: "Free to attend (ticketed, sold out)",
        age: "All ages",
        accessibility: "Step-free entry",
        booking: "",
        contactEmail: "Revawynne@gmail.com",
        contactPhone: "01204 468150",
        image: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-easter-egg-hunt-2025",
        title: "Easter Egg Hunt",
        orgSlug: "blackrod-sports-community-centre",
        category: "Family",
        start: onDate(2025, 4, 18, 11, 0),
        end: onDate(2025, 4, 18, 13, 0),
        venue: "Blackrod Sports & Community Centre",
        address: "Greenbarn Way, Blackrod BL6 5TG",
        description:
            "Easter Egg Hunt at the BSaCC — registration required; the event was oversubscribed.",
        cost: "Registration required",
        age: "Family",
        accessibility: "Step-free",
        booking: "",
        contactEmail: "bsacc.manager@gmail.com",
        contactPhone: "07980 713289",
        image: "https://images.unsplash.com/photo-1551888419-7b7a520fe0ca?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-mhfa-day1-2025",
        title: "Mental Health First Aid (MHFA) Course — Day 1",
        orgSlug: "blackrod-sports-community-centre",
        category: "Health & Wellbeing",
        start: onDate(2025, 2, 27, 9, 0),
        end: onDate(2025, 2, 27, 17, 0),
        venue: "Blackrod Sports & Community Centre",
        address: "Greenbarn Way, Blackrod BL6 5TG",
        description:
            "MHFA England-accredited two-day course (with Real Life Training). Day 1 of 2 (paired with 7 March). Valid 3 years; certificate plus app/membership benefits. Direct booking with the trainer — not a funded course.",
        cost: "Paid — contact trainer for prices",
        age: "Adults",
        accessibility: "Step-free",
        booking: "",
        contactEmail: "bsacc.manager@gmail.com",
        contactPhone: "07980 713289",
        image: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-mhfa-day2-2025",
        title: "Mental Health First Aid (MHFA) Course — Day 2",
        orgSlug: "blackrod-sports-community-centre",
        category: "Health & Wellbeing",
        start: onDate(2025, 3, 7, 9, 0),
        end: onDate(2025, 3, 7, 17, 0),
        venue: "Blackrod Sports & Community Centre",
        address: "Greenbarn Way, Blackrod BL6 5TG",
        description:
            "Day 2 of the MHFA England-accredited two-day course (with Real Life Training). Certificate plus app/membership benefits on completion.",
        cost: "Paid — contact trainer for prices",
        age: "Adults",
        accessibility: "Step-free",
        booking: "",
        contactEmail: "bsacc.manager@gmail.com",
        contactPhone: "07980 713289",
        image: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-efaw-apr-2025",
        title: "Emergency First Aid at Work — Open Course",
        orgSlug: "blackrod-sports-community-centre",
        category: "Health & Wellbeing",
        start: onDate(2025, 4, 24, 9, 0),
        end: onDate(2025, 4, 24, 17, 0),
        venue: "Blackrod Sports & Community Centre",
        address: "Greenbarn Way, Blackrod BL6 5TG",
        description:
            "Emergency First Aid at Work open course (with Real Life Training). Contact the trainer for prices and availability.",
        cost: "Paid — contact trainer",
        age: "Adults (workplace first aiders)",
        accessibility: "Step-free",
        booking: "",
        contactEmail: "bsacc.manager@gmail.com",
        contactPhone: "07980 713289",
        image: "https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-efaw-may-2025",
        title: "Emergency First Aid at Work — May Course",
        orgSlug: "blackrod-sports-community-centre",
        category: "Health & Wellbeing",
        start: onDate(2025, 5, 22, 9, 0),
        end: onDate(2025, 5, 22, 17, 0),
        venue: "Blackrod Sports & Community Centre",
        address: "Greenbarn Way, Blackrod BL6 5TG",
        description: "EFAW course (with Real Life Training). Contact the trainer for prices.",
        cost: "Paid — contact trainer",
        age: "Adults",
        accessibility: "Step-free",
        booking: "",
        contactEmail: "bsacc.manager@gmail.com",
        contactPhone: "07980 713289",
        image: "https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-scarecrow-workshops-2025",
        title: "Scarecrow-Making Workshops",
        orgSlug: "blackrod-scarecrow-festival-committee",
        category: "Family",
        start: onDate(2025, 4, 12, 10, 0),
        end: onDate(2025, 4, 12, 13, 0),
        venue: "Tree Barn",
        address: "Tree Barn, Blackrod",
        description:
            "Free scarecrow-making craft workshops in April 2025 in the run-up to the Scarecrow Festival.",
        cost: "Free",
        age: "Family",
        accessibility: "Indoor barn — accessible parking",
        booking: "",
        contactEmail: "jeanhibbert@virginmedia.com",
        contactPhone: "01204 691575",
        image: "https://images.unsplash.com/photo-1604881988758-f76ad2f7aac1?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-remembrance-2025",
        title: "Remembrance Sunday",
        orgSlug: "blackrod-town-council",
        category: "Community",
        start: onDate(2025, 11, 9, 14, 0),
        end: onDate(2025, 11, 9, 16, 0),
        venue: "Blackrod — civic act and church service",
        address: "Blackrod",
        description: "Public memorial event with civic act and linked church service.",
        cost: "Free",
        age: "All ages",
        accessibility: "Outdoor",
        booking: "",
        contactEmail: "clerk@blackrodtowncouncil.gov.uk",
        contactPhone: "01204 696362",
        image: "https://images.unsplash.com/photo-1542838686-37da4a9fd1a3?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-armistice-2025",
        title: "Armistice Day Service at the Cenotaph",
        orgSlug: "blackrod-town-council",
        category: "Community",
        start: onDate(2025, 11, 11, 11, 0),
        end: onDate(2025, 11, 11, 12, 0),
        venue: "Blackrod Cenotaph",
        address: "Manchester Road, Blackrod",
        description: "Public Armistice Day service at the Cenotaph.",
        cost: "Free",
        age: "All ages",
        accessibility: "Outdoor",
        booking: "",
        contactEmail: "clerk@blackrodtowncouncil.gov.uk",
        contactPhone: "01204 696362",
        image: "https://images.unsplash.com/photo-1542838686-37da4a9fd1a3?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-light-switchon-2025",
        title: "Blackrod Annual Christmas Light Switch-On 2025",
        orgSlug: "blackrod-town-council",
        category: "Community",
        start: onDate(2025, 11, 25, 18, 30),
        end: onDate(2025, 11, 25, 20, 0),
        venue: "Blackhorse Street Gardens",
        address: "Blackhorse Street, Blackrod",
        description: "Festive singing and refreshments afterwards. A village favourite.",
        cost: "Free",
        age: "All ages",
        accessibility: "Outdoor",
        booking: "",
        contactEmail: "clerk@blackrodtowncouncil.gov.uk",
        contactPhone: "01204 696362",
        image: "https://images.unsplash.com/photo-1606830733744-0ad778449672?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-christmas-market-2025",
        title: "Blackrod Christmas Market 2025",
        orgSlug: "horwich-blackrod-community-events-cic",
        category: "Community",
        start: onDate(2025, 12, 20, 10, 0),
        end: onDate(2025, 12, 20, 16, 0),
        venue: "Church Street, Blackrod",
        address: "Church Street, Blackrod",
        description:
            "Christmas market on Church Street with local stalls, festive treats and 'magical' family activity. Free to attend.",
        cost: "Free to attend",
        age: "All ages",
        accessibility: "Outdoor street",
        booking: "",
        contactEmail: "clerk@blackrodtowncouncil.gov.uk",
        contactPhone: "01204 696362",
        image: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=1200&q=80",
        featured: false,
        status: "approved",
    },

    // ===== RECURRING — NEXT OCCURRENCES (so "What's on this week" feels alive) =====
    {
        id: "evt-wiggle-mon",
        title: "Wiggle Worship Baby & Toddler Group",
        orgSlug: "wiggle-worship",
        category: "Family",
        start: nextWeekday(1, 9, 0),
        end: nextWeekday(1, 11, 15),
        venue: "St Katharine's Church",
        address: "Church Street, Blackrod BL6 5TE",
        description:
            "Term-time Monday morning songs, play and faith stories for babies and toddlers — combined with Baby Rhyme. Free.",
        cost: "Free",
        age: "0 – 4",
        accessibility: "Step-free, baby change",
        booking: "",
        contactEmail: "",
        contactPhone: "07514 041611",
        image: "https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-stitchknit-mon",
        title: "St Katharine's Stitch & Knit",
        orgSlug: "st-katharines-stitch-knit",
        category: "Community",
        start: nextWeekday(1, 10, 0),
        end: nextWeekday(1, 12, 0),
        venue: "St Katharine's Upper Room",
        address: "Church Street, Blackrod BL6 5TE",
        description:
            "Monday morning stitch & knit with around 12–13 regulars. Open to novices and newcomers. Items often made for charitable causes.",
        cost: "Free",
        age: "Adults",
        accessibility: "Step-free, hearing loop",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1517157212532-c5ee2cb55c01?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-connect-tue",
        title: "Connect — Warm Space at St Katharine's",
        orgSlug: "connect-st-katharines",
        category: "Health & Wellbeing",
        start: nextWeekday(2, 10, 30),
        end: nextWeekday(2, 12, 30),
        venue: "St Katharine's Church",
        address: "Church Street, Blackrod BL6 5TE",
        description:
            "A warm space with free refreshments and a friendly chat. Mainly retired villagers but everyone welcome.",
        cost: "Free",
        age: "Adults",
        accessibility: "Step-free, hearing loop",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-tuesday-communion",
        title: "Tuesday Holy Communion",
        orgSlug: "st-katharines-church",
        category: "Faith",
        start: nextWeekday(2, 9, 30),
        end: nextWeekday(2, 10, 15),
        venue: "St Katharine's Church",
        address: "Church Street, Blackrod BL6 5TE",
        description: "Mid-week Holy Communion service.",
        cost: "Free",
        age: "All ages",
        accessibility: "Step-free, hearing loop",
        booking: "",
        contactEmail: "Revawynne@gmail.com",
        contactPhone: "01204 468150",
        image: "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-knitnatter-sat",
        title: "Knit & Natter at the Library",
        orgSlug: "knit-natter-library",
        category: "Community",
        start: nextWeekday(6, 10, 30),
        end: nextWeekday(6, 13, 0),
        venue: "Blackrod Library",
        address: "Church Street, Blackrod BL6 5EQ",
        description: "Saturday morning knit, stitch and natter at the library. Average 13 attendees.",
        cost: "Free",
        age: "Adults",
        accessibility: "Step-free",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1565693413579-8a73bdee0a2c?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-choir-sat",
        title: "Blackrod Community Choir — Saturday Rehearsal",
        orgSlug: "blackrod-community-choir",
        category: "Music",
        start: nextWeekday(6, 10, 0),
        end: nextWeekday(6, 11, 0),
        venue: "St Katharine's Church",
        address: "Church Street, Blackrod BL6 5TE",
        description:
            "Friendly community choir — no audition, first session free as a taster. New members always welcome.",
        cost: "Free taster, then small subscription",
        age: "Adults",
        accessibility: "Step-free, hearing loop",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1200&q=80",
        featured: true,
        status: "approved",
    },
    {
        id: "evt-sunday-eucharist",
        title: "Sunday Eucharist",
        orgSlug: "st-katharines-church",
        category: "Faith",
        start: nextWeekday(0, 11, 0),
        end: nextWeekday(0, 12, 15),
        venue: "St Katharine's Church",
        address: "Church Street, Blackrod BL6 5TE",
        description: "Sunday Eucharist at 11am. Second-Sunday all-age worship at 9.30am.",
        cost: "Free",
        age: "All ages",
        accessibility: "Step-free, hearing loop",
        booking: "",
        contactEmail: "Revawynne@gmail.com",
        contactPhone: "01204 468150",
        image: "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-talking-football-mon",
        title: "Talking Football — Monday Session",
        orgSlug: "talking-football",
        category: "Health & Wellbeing",
        start: nextWeekday(1, 19, 0),
        end: nextWeekday(1, 20, 0),
        venue: "Bolton Arena",
        address: "Bolton Arena, Bolton",
        description:
            "Men's wellbeing through football — a friendly session with the option to talk if you want to. 200+ men have accessed the group since April 2022.",
        cost: "Free / small contribution",
        age: "Men, all ages",
        accessibility: "Indoor pitches",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1517649763962-0c623066013b?w=1200&q=80",
        featured: true,
        status: "approved",
    },
    {
        id: "evt-talking-football-wed",
        title: "Talking Football — Wednesday Session",
        orgSlug: "talking-football",
        category: "Health & Wellbeing",
        start: nextWeekday(3, 19, 0),
        end: nextWeekday(3, 20, 0),
        venue: "Bolton Arena",
        address: "Bolton Arena, Bolton",
        description: "Men's wellbeing football — Wednesday session.",
        cost: "Free / small contribution",
        age: "Men, all ages",
        accessibility: "Indoor pitches",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1517649763962-0c623066013b?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-bloomers-wed",
        title: "Blackrod Bloomers — Wednesday Planting",
        orgSlug: "blackrod-bloomers",
        category: "Volunteering",
        start: nextWeekday(3, 8, 0),
        end: nextWeekday(3, 10, 0),
        venue: "Around the village",
        address: "Blackrod",
        description: "Volunteer planting and maintenance across the village gateways and planters.",
        cost: "Free — volunteers welcome",
        age: "All ages (with help for under-12s)",
        accessibility: "Outdoor",
        booking: "",
        contactEmail: "clerk@blackrodtowncouncil.gov.uk",
        contactPhone: "01204 696362",
        image: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-bloomers-fri",
        title: "Blackrod Bloomers — Friday Planting",
        orgSlug: "blackrod-bloomers",
        category: "Volunteering",
        start: nextWeekday(5, 8, 0),
        end: nextWeekday(5, 10, 0),
        venue: "Around the village",
        address: "Blackrod",
        description: "Friday morning planting/maintenance with the Bloomers.",
        cost: "Free — volunteers welcome",
        age: "All ages",
        accessibility: "Outdoor",
        booking: "",
        contactEmail: "clerk@blackrodtowncouncil.gov.uk",
        contactPhone: "01204 696362",
        image: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-twilight-bookclub",
        title: "Twilight Book Club",
        orgSlug: "twilight-book-club",
        category: "Community",
        start: dt(((4 - today.getDay() + 7) % 7) + 14, 17, 45), // approx next 3rd Thu
        end: dt(((4 - today.getDay() + 7) % 7) + 14, 19, 0),
        venue: "Blackrod Library",
        address: "Church Street, Blackrod BL6 5EQ",
        description: "Third-Thursday evening book club at the library. Average 10–15 readers.",
        cost: "Free",
        age: "Adults",
        accessibility: "Step-free",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-reading-club",
        title: "St Katharine's Monday Night Reading Club",
        orgSlug: "monday-night-reading-club",
        category: "Community",
        start: dt(((1 - today.getDay() + 7) % 7) + 21, 19, 30), // last Mon-ish
        end: dt(((1 - today.getDay() + 7) % 7) + 21, 21, 0),
        venue: "St Katharine's Church",
        address: "Church Street, Blackrod BL6 5TE",
        description: "Last-Monday-of-the-month evening reading club. Free and open to new members.",
        cost: "Free",
        age: "Adults",
        accessibility: "Step-free",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-messy-church",
        title: "Messy Church",
        orgSlug: "messy-church-st-katharines",
        category: "Family",
        start: dt(((6 - today.getDay() + 7) % 7) + 21, 16, 0), // last Sat-ish
        end: dt(((6 - today.getDay() + 7) % 7) + 21, 18, 0),
        venue: "St Katharine's Church",
        address: "Church Street, Blackrod BL6 5TE",
        description:
            "Last-Saturday family Messy Church — craft, story, song and a meal. Around 30–35 children, 30 adults, 20 volunteers.",
        cost: "Free",
        age: "Family",
        accessibility: "Step-free, baby change",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-history-meeting",
        title: "Blackrod Local History Group — Monthly Meeting",
        orgSlug: "blackrod-local-history-group",
        category: "Heritage",
        start: dt(10, 19, 30),
        end: dt(10, 21, 0),
        venue: "Blackrod Library / Council Chamber",
        address: "Church Street, Blackrod BL6 5EQ",
        description:
            "Monthly local history talk. £1 entry or £10 annual membership — refreshments included. Average attendance about 40.",
        cost: "£1 entry or £10 annual",
        age: "Adults",
        accessibility: "Step-free",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    {
        id: "evt-walking-group",
        title: "Blackrod Community Walking Group — Monthly Walk",
        orgSlug: "blackrod-community-walking-group",
        category: "Health & Wellbeing",
        start: nextWeekday(6, 10, 0),
        end: nextWeekday(6, 13, 0),
        venue: "Meet point on group page",
        address: "Blackrod",
        description:
            "A monthly local walk with the option of lunch afterwards. Friendly, sociable pace.",
        cost: "Free",
        age: "Adults",
        accessibility: "Outdoor — pace varies",
        booking: "",
        contactEmail: "",
        contactPhone: "",
        image: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=1200&q=80",
        featured: false,
        status: "approved",
    },
    // Pending submission for admin demo
    {
        id: "evt-pending-art",
        title: "Art Group — Annual Library Exhibition Opening (Pending)",
        orgSlug: "blackrod-art-group",
        category: "Community",
        start: dt(20, 18, 0),
        end: dt(20, 20, 0),
        venue: "Blackrod Library",
        address: "Church Street, Blackrod BL6 5EQ",
        description: "Opening evening of the Blackrod Art Group's 12-week library exhibition.",
        cost: "Free",
        age: "All ages",
        accessibility: "Step-free",
        booking: "",
        contactEmail: "blackrodartgroup@gmail.com",
        contactPhone: "07894 904100",
        image: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=1200&q=80",
        featured: false,
        status: "pending",
    },
];

// ---------- VENUES ----------
export const VENUES = [
    {
        id: "ven-bsacc",
        name: "Blackrod Sports and Community Centre",
        address: "Greenbarn Way, Blackrod BL6 5TG",
        facilities: ["Main hall", "Studios", "Karate/youth space", "Kitchen", "Outdoor pitches"],
        accessibility: "Step-free access; please contact for specific access needs",
        capacity: 200,
        booking: "mailto:bsacc.manager@gmail.com",
        image: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=1200&q=80",
    },
    {
        id: "ven-st-kats",
        name: "St Katharine's Church",
        address: "Church Street, Blackrod BL6 5TE",
        facilities: ["Church", "Upper room", "Hall", "Kitchenette"],
        accessibility: "Step-free, hearing loop",
        capacity: 220,
        booking: "https://www.stkatharine.net",
        image: "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=1200&q=80",
    },
    {
        id: "ven-library",
        name: "Blackrod Library / Council Chamber",
        address: "Church Street, Blackrod BL6 5EQ",
        facilities: ["Library", "Council chamber", "Meeting room"],
        accessibility: "Step-free",
        capacity: 60,
        booking: "mailto:clerk@blackrodtowncouncil.gov.uk",
        image: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=1200&q=80",
    },
    {
        id: "ven-cricket-club",
        name: "Blackrod Cricket Club",
        address: "Vicarage Road, Blackrod BL6 5AA",
        facilities: ["Pavilion", "Function room", "Outdoor cricket ground"],
        accessibility: "Step-free access to pavilion",
        capacity: 4400,
        booking: "",
        image: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=1200&q=80",
    },
    {
        id: "ven-hilltops",
        name: "Hilltops Sports & Social Club",
        address: "Blackrod",
        facilities: ["Function room", "Bar", "Bowling green"],
        accessibility: "Step-free",
        capacity: 180,
        booking: "",
        image: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1200&q=80",
    },
    {
        id: "ven-blackhorse-gardens",
        name: "Blackhorse Street Gardens",
        address: "Blackhorse Street, Blackrod",
        facilities: ["Outdoor gardens", "Public seating"],
        accessibility: "Outdoor — accessible paths",
        capacity: 500,
        booking: "",
        image: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=1200&q=80",
    },
    {
        id: "ven-cenotaph",
        name: "Blackrod Cenotaph",
        address: "Manchester Road, Blackrod",
        facilities: ["Public memorial space"],
        accessibility: "Outdoor",
        capacity: 300,
        booking: "",
        image: "https://images.unsplash.com/photo-1542838686-37da4a9fd1a3?w=1200&q=80",
    },
    {
        id: "ven-tree-barn",
        name: "Tree Barn",
        address: "Blackrod",
        facilities: ["Workshop space", "Outdoor area"],
        accessibility: "Accessible parking",
        capacity: 60,
        booking: "",
        image: "https://images.unsplash.com/photo-1604881988758-f76ad2f7aac1?w=1200&q=80",
    },
];

// ---------- VOLUNTEER OPPORTUNITIES ----------
export const VOLUNTEER_OPPS = [
    {
        id: "vol-bloomers",
        title: "Volunteer Gardener — Blackrod Bloomers",
        orgSlug: "blackrod-bloomers",
        description:
            "Help plant and maintain Blackrod's gateways, planters and flower beds. Welcoming team of 7 — always open to new volunteers and donations.",
        age: "All ages (with help for under-12s)",
        time: "Wednesdays & Fridays 8am (a couple of hours)",
        skills: "Reliable, happy outdoors — no experience needed",
    },
    {
        id: "vol-messy-church",
        title: "Messy Church Helper",
        orgSlug: "messy-church-st-katharines",
        description:
            "Join the team of ~20 volunteers running monthly Messy Church for 30+ children and their families. Great for DofE Volunteering section.",
        age: "14+",
        time: "Last Saturday of the month, 3pm – 6.30pm",
        skills: "Friendly, enjoy working with families",
    },
    {
        id: "vol-knit-charity",
        title: "Charity Knitter — Stitch & Knit",
        orgSlug: "st-katharines-stitch-knit",
        description:
            "Help make and finish items to sell at community events for charitable causes. Open to novices.",
        age: "16+",
        time: "Mondays 10am – 12 noon",
        skills: "Willing to learn — happy to chat",
    },
    {
        id: "vol-art-group",
        title: "Art Group Exhibition Steward",
        orgSlug: "blackrod-art-group",
        description: "Steward the Scarecrow Festival weekend exhibition and the 12-week library exhibition.",
        age: "16+",
        time: "Occasional days through the year",
        skills: "Friendly, comfortable chatting to visitors",
    },
    {
        id: "vol-wiggle",
        title: "Wiggle Worship & Baby Rhyme Volunteer",
        orgSlug: "wiggle-worship",
        description: "Help with songs, refreshments and welcoming new families. Great Skills/Volunteering pick for DofE.",
        age: "14+",
        time: "Monday mornings in term time",
        skills: "Patient, friendly, enjoy babies and toddlers",
    },
    {
        id: "vol-choir",
        title: "Community Choir New Member (taster welcome)",
        orgSlug: "blackrod-community-choir",
        description: "Not a volunteer role exactly — join the choir! First session free, no audition, all welcome.",
        age: "All ages",
        time: "Saturdays 10am – 11am",
        skills: "Just enjoy singing",
    },
    {
        id: "vol-history",
        title: "Local History Newsletter Helper",
        orgSlug: "blackrod-local-history-group",
        description:
            "Help compile the monthly newsletter (sent to 80+ email addresses) and assist on meeting nights.",
        age: "18+",
        time: "A few hours each month",
        skills: "Comfortable with email; interest in local history",
    },
    {
        id: "vol-walking",
        title: "Walking Group Buddy",
        orgSlug: "blackrod-community-walking-group",
        description:
            "Help lead or support our monthly community walks — particularly welcoming to less confident walkers.",
        age: "18+",
        time: "Monthly",
        skills: "Friendly and patient pace; basic first-aid useful",
    },
    {
        id: "vol-young-peoples-centre",
        title: "Youth Centre Helper",
        orgSlug: "blackrod-young-peoples-centre",
        description:
            "Help run free junior and senior youth club sessions for children aged 4 – 17 (reception+).",
        age: "16+",
        time: "Evenings/weekends",
        skills: "DBS-checked or willing to be; warm with young people",
    },
    {
        id: "vol-festival",
        title: "Festival of Blackrod Volunteer 2026",
        orgSlug: "horwich-blackrod-community-events-cic",
        description:
            "Join the team behind Blackrod's biggest day out — 12 September 2026. Set-up, stewarding, info point.",
        age: "16+",
        time: "Saturday 12 September 2026",
        skills: "Friendly, reliable, happy to be on your feet",
    },
];

// ---------- LOCAL FEED ----------
export const FEED_POSTS = [
    {
        id: "feed-festival-recap",
        orgSlug: "horwich-blackrod-community-events-cic",
        type: "Community updates",
        title: "Festival of Blackrod 2025: ~4,400 of you turned up",
        body:
            "Massive thanks to everyone who came to the Festival of Blackrod 2025 at the Cricket Club. ~4,400 attendees, free to all — total cost £18,753.27 with £7,000 from the Town Council and the rest from sponsorship. Bring on 12 September 2026!",
        time: dt(-5, 16, 0),
        image: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200&q=80",
    },
    {
        id: "feed-walking-jan26",
        orgSlug: "blackrod-community-walking-group",
        type: "Club update",
        title: "13 walkers out in the January chill",
        body:
            "13 of us braved the weather for January's walk — followed by a proper warming lunch. New walkers always welcome, pace stays friendly.",
        time: dt(-3, 14, 0),
    },
    {
        id: "feed-choir-6yr",
        orgSlug: "blackrod-community-choir",
        type: "Community updates",
        title: "Six years of Blackrod Community Choir 🎶",
        body:
            "We're now into our sixth year! Numbers are growing, there's no upper limit and the first session is always free. Saturdays at 10am, St Katharine's.",
        time: dt(-7, 9, 30),
    },
    {
        id: "feed-bloomers-help",
        orgSlug: "blackrod-bloomers",
        type: "Volunteer request",
        title: "Bloomers need new hands for spring planting",
        body:
            "Just 7 of us keep the village's planters and gateways looking sharp. Could you spare a couple of hours Wednesday or Friday morning at 8am? Tea provided.",
        time: dt(-2, 8, 0),
    },
    {
        id: "feed-art-50",
        orgSlug: "blackrod-art-group",
        type: "Club update",
        title: "Record 56 members in 2025",
        body:
            "A record-breaking year — 56 members, our Scarecrow weekend exhibition, and the annual 12-week library show coming again soon.",
        time: dt(-10, 11, 0),
        image: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=1200&q=80",
    },
    {
        id: "feed-trust-grants",
        orgSlug: "blackrod-educational-trust",
        type: "Charity appeal",
        title: "~£10,000 granted in 2024 — applications still welcome",
        body:
            "Last year the Trust awarded around £10,000 across 49 families. Criteria may be reviewed for the next round — get in touch via the Town Clerk.",
        time: dt(-12, 10, 0),
    },
    {
        id: "feed-talking-football-200",
        orgSlug: "talking-football",
        type: "Club update",
        title: "200+ men have joined us since April 2022",
        body:
            "From a quiet kickabout to mental-health workshops, charity games and a planned band-night fundraiser. Mon & Wed, 7–8pm at Bolton Arena.",
        time: dt(-6, 19, 30),
    },
    {
        id: "feed-bsacc-notice",
        orgSlug: "blackrod-sports-community-centre",
        type: "Public notice",
        title: "Weekly timetable refreshed",
        body:
            "Walking football, ICT, line dancing, pilates, tea & chat, dementia support, art group, fitness, table tennis, karate, youth & sport — see the latest What's On.",
        time: dt(-4, 12, 0),
    },
    {
        id: "feed-ptfa-events",
        orgSlug: "blackrod-church-school-ptfa",
        type: "School news",
        title: "Glow Disco, Valentine's Disco and Easter Bingo on the way",
        body:
            "The PTFA is planning a full calendar of family events — keep an eye on the school's PTFA page for dates and tickets.",
        time: dt(-8, 15, 0),
    },
    {
        id: "feed-messy-church",
        orgSlug: "messy-church-st-katharines",
        type: "Community updates",
        title: "30 kids, 30 grown-ups, 20 volunteers — one brilliant Saturday",
        body:
            "Another lovely Messy Church on the last Saturday. Crafts, story, song and a meal. Next one same time, same place.",
        time: dt(-15, 18, 0),
    },
];
